import React, { createContext, ReactNode, useContext, useMemo } from 'react'

import { LoadingComponent } from '@/components/loading/Loading'
import { chains } from '@/config'
import { useListToken } from '@/data/tokens'
import { INetwork, IPair, IToken, TokenPairMap } from '@/types'

interface TokenContextType {
  listToken: IToken[]
  listNetwork: INetwork[]
  pairs: TokenPairMap
  isLoadingToken: boolean
}

const TokenContext = createContext<TokenContextType | undefined>(undefined)

export const useApiTokenContext = () => {
  const context = useContext(TokenContext)
  if (context === undefined) {
    throw new Error('useApiTokenContext must be used within an ApiTokenProvider')
  }
  return context
}

export const useNativeToken = (networkId: string | null) => {
  const { listToken } = useApiTokenContext()
  return useMemo(
    () => listToken.find((token) => token.networkId === networkId && token.tokenAddress === 'native'),
    [listToken, networkId]
  )
}

export const useCheckPairSupport = () => {
  const { pairs } = useApiTokenContext()

  return (fromTokenId: string | undefined, toTokenId: string | undefined) => {
    const array = pairs[fromTokenId ?? ''] ?? []
    return array.some((e) => e.isActive && e.toTokenId === toTokenId && e.fromTokenId === fromTokenId)
  }
}

interface TokenProviderProps {
  children: ReactNode
}

export const TokenProvider: React.FC<TokenProviderProps> = ({ children }) => {
  const { data, isFetched } = useListToken()
  const isLoadingToken = !isFetched
  const { listToken, pairs } = useMemo(() => {
    const pairsMap: TokenPairMap = {}
    data?.pairs?.forEach((pair: IPair) => {
      pairsMap[pair.fromTokenId] ||= []
      pairsMap[pair.fromTokenId].push(pair)
    })
    return { listToken: data?.tokens ?? [], pairs: pairsMap }
  }, [data])

  const listNetwork = useMemo(() => {
    const networksMap = new Map<string, INetwork>()

    listToken?.forEach((token) => {
      if (!networksMap.has(token.networkId)) {
        const chain = chains.find((c) => c.network === token.networkId)

        networksMap.set(token.networkId, {
          ...token,
          wallets: chain?.wallets || [],
          actions: chain?.actions || {},
        })
      }
    })

    return Array.from(networksMap.values())
  }, [listToken])

  const value = {
    listToken,
    listNetwork,
    pairs, // fromTokenId => Pair[]
    isLoadingToken,
  }

  if (!listToken?.length) {
    return <LoadingComponent />
  }

  return <TokenContext.Provider value={value}>{children}</TokenContext.Provider>
}
